<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import { EditorState } from '@common/Common'

    import type { NotificationEditorViewModel } from '@Alternatives/Notifications/Components/NotificationEditor'
    import { CreateNotificationEventPayload, UpdateNotificationEventPayload } from '@Alternatives/Notifications/Components/NotificationEditor'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: NotificationEditorViewModel
    }>()

    const emit = defineEmits<{
        (e: 'create-notification', payload: CreateNotificationEventPayload): void
        (e: 'update-notification', payload: UpdateNotificationEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const modalTitle = computed(() =>
        vm.value.state === EditorState.Creator
            ? 'Create notification'
            : 'Edit notification'
    )

    const confirmButtonLabel = computed(() =>
        vm.value.state === EditorState.Creator
            ? 'Create'
            : 'Update'
    )

    const statuses = computed(() => vm.value.statuses)
    const attributes = computed(() => vm.value.attributes)
    const emailSnippets = computed(() => vm.value.emailSnippets)
    const error = computed(() => vm.value.error)

    // ---------------- writable computed (VM fields) ----------------

    const enabled = computed({
        get: () => vm.value.enabled,
        set: v => { vm.value.enabled = v }
    })

    const name = computed({
        get: () => vm.value.name,
        set: v => { vm.value.name = v }
    })

    const description = computed({
        get: () => vm.value.description,
        set: v => { vm.value.description = v }
    })

    const statusId = computed({
        get: () => vm.value.statusId,
        set: v => { vm.value.statusId = v }
    })

    const prevStatusId = computed({
        get: () => vm.value.prevStatusId,
        set: v => { vm.value.prevStatusId = v }
    })

    const emailSubject = computed({
        get: () => vm.value.emailSubject,
        set: v => { vm.value.emailSubject = v }
    })

    const emailReplyToEmail = computed({
        get: () => vm.value.emailReplyToEmail,
        set: v => { vm.value.emailReplyToEmail = v }
    })

    const emailRecipientAttributeId = computed({
        get: () => vm.value.emailRecipientAttributeId,
        set: v => { vm.value.emailRecipientAttributeId = v }
    })

    const emailAdHocRecipients = computed({
        get: () => vm.value.emailAdHocRecipients,
        set: v => { vm.value.emailAdHocRecipients = v }
    })

    const emailMessage = computed({
        get: () => vm.value.emailMessage,
        set: v => { vm.value.emailMessage = v }
    })

    // ---------------- methods ----------------

    function insertEmailSnippet(content: string)
    {
        emailMessage.value = (emailMessage.value ?? '') + content
    }

    function onFormSubmit()
    {
        vm.value.clearValidationErrors()

        if (vm.value.state === EditorState.Creator)
        {
            emit(
                'create-notification',
                new CreateNotificationEventPayload(
                    name.value,
                    description.value,
                    enabled.value,
                    statusId.value,
                    prevStatusId.value,
                    emailSubject.value,
                    emailMessage.value,
                    emailRecipientAttributeId.value,
                    emailAdHocRecipients.value,
                    emailReplyToEmail.value
                )
            )
        }
        else
        {
            emit(
                'update-notification',
                new UpdateNotificationEventPayload(
                    vm.value.id,
                    name.value,
                    description.value,
                    enabled.value,
                    statusId.value,
                    prevStatusId.value,
                    emailSubject.value,
                    emailMessage.value,
                    emailRecipientAttributeId.value,
                    emailAdHocRecipients.value,
                    emailReplyToEmail.value
                )
            )
        }
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div v-if="error" class="alert alert-danger" role="alert">
                            <strong>{{error.ErrorCode.substring(0, error.ErrorCode.indexOf("."))}}.</strong> {{error.Error}}
                        </div>

                        <form id="notification-editor-form" @submit.prevent="onFormSubmit">
                            <div class="row">
                                <div class="col-sm-6">
                                    <div class="form-group">
                                        <label>
                                            <input type="checkbox" v-model="enabled"> Active
                                        </label>
                                    </div>

                                    <div class="form-group">
                                        <label>Name (2 - 100 characters):</label>
                                        <input type="text" class="form-control" v-model.trim="name" required minlength="2" maxlength="100">
                                    </div>

                                    <div class="form-group">
                                        <label>Description (optional):</label>
                                        <textarea class="form-control" rows="6" v-model.trim="description"></textarea>
                                    </div>

                                    <div class="form-group">
                                        <label>Send when status is changed to:</label>
                                        <select v-model="statusId" class="form-control">
                                            <option v-for="s in statuses" :value="s.Id">{{s.Name}}</option>
                                        </select>
                                    </div>

                                    <div class="form-group">
                                        <label>Send when previous status was:</label>
                                        <select v-model="prevStatusId" class="form-control">
                                            <option :value="null">Any</option>
                                            <option v-for="s in statuses" :value="s.Id">{{s.Name}}</option>
                                        </select>
                                    </div>
                                </div>

                                <div class="col-sm-6">
                                    <div class="form-group">
                                        <label>Email subject:</label>
                                        <input type="text" class="form-control" v-model.trim="emailSubject" required minlength="2" maxlength="500">
                                    </div>
                                    <div class="form-group">
                                        <label>Reply to email:</label>
                                        <input type="email" class="form-control" v-model.trim="emailReplyToEmail" required>
                                    </div>

                                    <div class="form-group">
                                        <label>Recipients:</label><br />
                                        <small class="text-muted">Email recipients could be provided in two ways: from attribute and/or list of ad hoc emails separated by comma.</small>
                                        <select v-model="emailRecipientAttributeId" class="form-control">
                                            <option :value="null">Select attribute...</option>
                                            <option v-for="attr in attributes" :value="attr.Id">{{attr.Name}}</option>
                                        </select>
                                        <input type="text" class="form-control" placeholder="Add hoc emails separated by comma" v-model.trim="emailAdHocRecipients" />
                                    </div>

                                    <div class="form-group">
                                        <label>Message:</label>
                                        <small class="text-muted">Message that will be send to the recipients.</small>
                                        <div class="pull-right">
                                            <div class="dropdown">
                                                <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Insert</button>
                                                <div class="dropdown-menu" style="height: 50vh; min-width: 300px; min-height: 300px; overflow-y: auto;">
                                                    <a v-for="es in emailSnippets" class="dropdown-item" @click="insertEmailSnippet(es.Content)">{{es.Title}}</a>
                                                </div>
                                            </div>
                                        </div>
                                        <textarea class="form-control" rows="6" v-model.trim="emailMessage" required></textarea>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="notification-editor-form" class="btn btn-primary">{{ confirmButtonLabel }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template> 